import cn from "classnames";
import Image from "next/image";
import Typography from "@mui/material/Typography";

import { ImgBgResponsive } from "@/src/component/base/ImgBgResponsive";
import { ProviderHooks } from "@/src/hook";
import { VerticalNestedItem } from "@/src/type/strapi/component/section/VerticaleNested";
import { LoadFromCDN } from "@/src/util";

import styles from "./DisplayItem.module.scss";
import { ButtonLinkModule } from "../../component";

interface DisplayItemProps {
  item: VerticalNestedItem;
  onClick?: () => void;
  childItems?: VerticalNestedItem[];
  lazy?: "lazy" | "eager";
  quality?: number;
  isExpanded?: boolean;
  expandedItem?: string;
  animate?: {
    canExpand?: boolean;
    showText?: boolean;
    addTransition?: boolean;
    contentOn?: boolean;
  };
  isOutline?: boolean;
  boundingClientRect?: DOMRect;
  fullLoad?: boolean;
}

function DisplayItem({ item, onClick, isExpanded, isOutline, lazy, quality, childItems, animate, boundingClientRect, fullLoad }: DisplayItemProps) {
  const { canExpand, showText, addTransition, contentOn } = animate ?? {};
  const cms = ProviderHooks.useCMS();

  const columns = () => {
    switch ((childItems ?? []).length) {
      case 2:
        return styles.two;
      case 3:
        return styles.three;
      case 4:
        return styles.four;
      default:
        return;
    }
  };

  const getHref = (href: string, isExternal: boolean) => {
    return isExternal ? href : cms.constructLink(href);
  };

  const forceLink = (href: string, target: string) => {
    window.open(href, target);
  };

  const getGradient = (gradient: string) => {
    switch (gradient) {
      case "overlayLinearGradient1":
        return styles.gradient1;
      case "overlayLinearGradient2":
        return styles.gradient2;
      case "overlayLinearGradient3":
        return styles.gradient3;
      case "overlayLinearGradient4":
        return styles.gradient4;
      default:
        return;
    }
  };

  return (
    <div
      className={cn(
        isOutline ? styles.baseItemOutline : styles.baseItem,
        canExpand ? styles.expanded : undefined,
        addTransition ? styles.addTransition : undefined,
        isExpanded ? styles.expandedStart : undefined
      )}
      style={
        boundingClientRect
          ? {
              position: "fixed",
              top: boundingClientRect?.top,
              left: boundingClientRect?.left,
              right: boundingClientRect?.right - boundingClientRect?.width,
              bottom: boundingClientRect?.top + boundingClientRect?.height,
              height: boundingClientRect?.height,
              width: boundingClientRect?.width,
              zIndex: 1000000,
              transition: "all 1.0s ease-in-out",
            }
          : {}
      }
      onClick={isExpanded ? undefined : onClick}
    >
      {item.staticBackground?.data?.attributes?.url && (
        <ImgBgResponsive
          className={cn(styles.baseItemImage, !isExpanded ? styles.allowHover : undefined)}
          imageUrl={LoadFromCDN.imageQualityAndSizeOptional(item.staticBackground.data.attributes.url, quality, !fullLoad ? 660 : 1920)}
          triggers={["swiperSlideChange", "click", "touchstart", "scroll"]}
          prefetch={lazy === "eager"}
          lazy={lazy ?? "lazy"}
          onClick={isExpanded ? undefined : onClick}
        >
          <div className={cn(styles.baseItemImageOverlay, getGradient(item.backgroundGrandient))} />
        </ImgBgResponsive>
      )}
      <div
        className={cn(
          styles.baseItemContent,
          !isExpanded || contentOn ? styles.contentOn : styles.contentOff,
          showText || !isExpanded ? styles.showText : styles.hideText,
          isExpanded ? styles.expanded : styles.notExpanded
        )}
      >
        {contentOn && (
          <div
            className={styles.backButtonContianer}
            onClick={onClick}
          >
            <Image
              src={"/image/icon_back.svg"}
              alt="back"
              width={24}
              height={24}
            />
            <Typography className={styles.backButtonText}>Back</Typography>
          </div>
        )}
        <Typography
          className={styles.baseItemTitle}
          onClick={onClick}
        >
          {item.title}
        </Typography>
        {item.subtitle && (
          <Typography
            className={styles.baseItemSubtitle}
            onClick={onClick}
          >
            {item.subtitle}
          </Typography>
        )}
        {item.showCtaAsButton && item.cta && (
          <div className={styles.ctaButtonContainer}>
            <ButtonLinkModule
              buttonLink={{
                href: item.cta?.href,
                isExternal: item.cta?.isExternal,
                target: item.cta?.target ?? "_self",
                color: "secondary",
                variant: "outlined",
                label: item.cta?.label,
                name: item.cta?.name,
              }}
              forceButton={true}
            />
          </div>
        )}
        {contentOn && (
          <div className={cn(styles.childContainer, columns())}>
            {(childItems ?? [])?.map((childItem) => (
              <div
                key={childItem.id}
                className={cn(styles.childItem, columns())}
                onClick={() => forceLink(getHref(childItem.cta?.href, childItem.cta?.isExternal), childItem.cta?.target)}
              >
                <Typography className={styles.childTitle}>{childItem.title}</Typography>
                {childItem.subtitle && <Typography className={styles.childSubtitle}>{childItem.subtitle}</Typography>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default DisplayItem;
